/**
 * Dialog for creating a new task.
 *
 * Fields: title (required), phase, status, priority, due date, assignee.
 * On success the task list is refetched and the new task is opened in the drawer.
 */
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { TradeCategory } from "@/hooks/use-config";
import { useContacts, useProjectContacts } from "@/hooks/use-contacts";
import type { ProjectTask } from "@/hooks/use-tasks";
import { useCreateProjectTask } from "@/hooks/use-tasks";
import { ContactPicker, TradePicker } from "./assignee-picker";
import type { ProjectPhase, TaskPriority, TaskStatus } from "./task-chips";

const PHASE_OPTIONS: Array<{ value: ProjectPhase; label: string }> = [
  { value: "pre_construction", label: "Pre-Construction" },
  { value: "design", label: "Design" },
  { value: "construction", label: "Construction" },
  { value: "post_construction", label: "Close-out" },
  { value: "completed", label: "Completed" },
];

const STATUS_OPTIONS: Array<{ value: TaskStatus; label: string }> = [
  { value: "not_started", label: "Todo" },
  { value: "in_progress", label: "In Progress" },
  { value: "in_review", label: "In Review" },
  { value: "blocked", label: "Blocked" },
];

const PRIORITY_OPTIONS: Array<{ value: TaskPriority; label: string }> = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export function NewTaskDialog({
  projectId,
  open,
  onOpenChange,
  trades,
  onCreated,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trades: TradeCategory[];
  onCreated?: (task: ProjectTask) => void;
}) {
  const createTask = useCreateProjectTask(projectId);
  const { data: projectContacts } = useProjectContacts(projectId);
  const { data: allContacts } = useContacts();
  // Show project-scoped contacts when assignments exist; fall back to all contacts.
  const contacts = projectContacts?.length ? projectContacts : (allContacts ?? []);

  const [title, setTitle] = useState("");
  const [phase, setPhase] = useState<ProjectPhase>("construction");
  const [status, setStatus] = useState<TaskStatus>("not_started");
  const [priority, setPriority] = useState<TaskPriority>("medium");
  const [dueDate, setDueDate] = useState<string | null>(null);
  const [assignedTradeId, setAssignedTradeId] = useState<string | null>(null);
  const [assignedToContactId, setAssignedToContactId] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement | null>(null);

  function reset() {
    setTitle("");
    setPhase("construction");
    setStatus("not_started");
    setPriority("medium");
    setDueDate(null);
    setAssignedTradeId(null);
    setAssignedToContactId(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      titleRef.current?.focus();
      return;
    }
    createTask.mutate(
      {
        title: title.trim(),
        phase,
        status,
        priority,
        dueDate: dueDate ?? undefined,
        assignedTradeId,
        assignedToContactId,
      },
      {
        onSuccess: (res) => {
          const created = (res as { data: ProjectTask }).data;
          handleOpenChange(false);
          if (onCreated && created) onCreated(created);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="pbos-tasks sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* Title */}
          <div className="space-y-1">
            <label className="text-[12px] font-medium text-[var(--text-2)]" htmlFor="nt-title">
              Title <span className="text-[var(--rose-ink)]">*</span>
            </label>
            <input
              id="nt-title"
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What needs to be done?"
              autoFocus
              className="w-full rounded-md border border-[var(--border-2)] bg-[var(--paper-0)] px-3 py-2 text-[13px] outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent)]"
            />
          </div>

          {/* Phase + Status row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[12px] font-medium text-[var(--text-2)]" htmlFor="nt-phase">
                Phase
              </label>
              <Select value={phase} onValueChange={(v) => setPhase(v as ProjectPhase)}>
                <SelectTrigger id="nt-phase" className="text-[12.5px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-[12px] font-medium text-[var(--text-2)]" htmlFor="nt-status">
                Status
              </label>
              <Select value={status} onValueChange={(v) => setStatus(v as TaskStatus)}>
                <SelectTrigger id="nt-status" className="text-[12.5px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Priority + Due date row */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[12px] font-medium text-[var(--text-2)]" htmlFor="nt-priority">
                Priority
              </label>
              <Select value={priority} onValueChange={(v) => setPriority(v as TaskPriority)}>
                <SelectTrigger id="nt-priority" className="text-[12.5px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <p className="text-[12px] font-medium text-[var(--text-2)]">Due date</p>
              <DatePicker
                value={dueDate}
                onChange={(d) => setDueDate(d ?? null)}
                ariaLabel="Due date"
                className="w-full"
              />
            </div>
          </div>

          {/* Trade + Contact */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <p className="text-[12px] font-medium text-[var(--text-2)]">Trade</p>
              <TradePicker value={assignedTradeId} trades={trades} onChange={setAssignedTradeId} />
            </div>
            <div className="space-y-1">
              <p className="text-[12px] font-medium text-[var(--text-2)]">Contact</p>
              <ContactPicker
                value={assignedToContactId}
                contacts={contacts}
                onChange={setAssignedToContactId}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createTask.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || createTask.isPending}>
              {createTask.isPending ? "Creating…" : "Create task"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
